import { AfterRenderRef, AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Injector, OnDestroy, OnInit, ViewEncapsulation, afterNextRender } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { ScreenSaverService } from '@app/core/services/screen-saver.service';
import { CommandCenterShellService } from '@app/core/services/command-center-shell.service';

@Component({
    selector: 'compact-layout',
    templateUrl: './compact.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CompactLayoutComponent implements OnInit, AfterViewInit, OnDestroy
{
    isScreenSmall: boolean;
    navigation: Navigation;
    public isCommandCenterMode = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private _commandCenterNavigationVisible = true;
    private _commandCenterPreviousNavigationOpened: boolean | null = null;
    private _viewInitialized = false;
    private _commandCenterNavigationRenderRef?: AfterRenderRef;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private commandCenterShell: CommandCenterShellService,
        private injector: Injector,
        private destroyRef: DestroyRef,
        public screenSaver: ScreenSaverService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    get showShellHeader(): boolean
    {
        return !this.isCommandCenterMode || this._commandCenterNavigationVisible;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
                this.syncCommandCenterNavigation();
            });

        this.commandCenterShell.state$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((state) => {
                this.isCommandCenterMode = state.active;
                this._commandCenterNavigationVisible = state.navigationVisible;
                this.syncCommandCenterNavigation();
            });
    }

    ngAfterViewInit(): void
    {
        this._viewInitialized = true;
        this.syncCommandCenterNavigation();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        this._commandCenterNavigationRenderRef?.destroy();
        this._commandCenterNavigationRenderRef = undefined;

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    private syncCommandCenterNavigation(): void
    {
        if (!this._viewInitialized)
        {
            return;
        }

        this._commandCenterNavigationRenderRef?.destroy();
        this._commandCenterNavigationRenderRef = afterNextRender(() => {
            this._commandCenterNavigationRenderRef = undefined;
            if (this.destroyRef.destroyed)
            {
                return;
            }

            const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

            if ( !navigation )
            {
                return;
            }

            if ( this.isCommandCenterMode )
            {
                if ( this._commandCenterPreviousNavigationOpened === null )
                {
                    this._commandCenterPreviousNavigationOpened = navigation.opened;
                }

                if ( this._commandCenterNavigationVisible )
                {
                    navigation.open();
                }
                else
                {
                    navigation.close();
                }

                return;
            }

            if ( this._commandCenterPreviousNavigationOpened !== null )
            {
                if ( this._commandCenterPreviousNavigationOpened )
                {
                    navigation.open();
                }
                else
                {
                    navigation.close();
                }

                this._commandCenterPreviousNavigationOpened = null;
            }
        }, { injector: this.injector });
    }
}
